import { useCoreApiClient } from "@api/use-core-api-client";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { FaroButton } from "@faro-lotv/flat-ui";
import { useAppNavigation } from "@hooks/navigation/use-app-navigation";
import { useToast } from "@hooks/use-toast";
import { Box } from "@mui/material";
import { SettingsTabs } from "@router/route-params";
import { SphereDashboardAPITypes } from "@stellar/api-logic";
import { selectedSdbCompanyIdSelector } from "@store/sdb-company/sdb-company-selector";
import { useAppSelector } from "@store/store-helper";
import {
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { PaymentIntentResult } from "@stripe/stripe-js";
import { WorkspaceEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { FormEvent, useState } from "react";

interface Props {
  /** The credit package to be purchased */
  creditPackage: SphereDashboardAPITypes.StripeProduct;
}

/** Renders stripe checkout payment element to process the payment */
export function StripeCheckout({ creditPackage }: Props): JSX.Element {
  const stripe = useStripe();
  const elements = useElements();
  const { showToast } = useToast();
  const { trackEvent } = useTrackEvent();
  const { navigateToSettingsPage } = useAppNavigation();
  const { handleErrorWithToast } = useErrorContext();
  const coreApiClient = useCoreApiClient();
  const companyId = useAppSelector(selectedSdbCompanyIdSelector);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  async function handleSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();

    // Track the event when the user clicks on pay now button
    trackEvent({
      name: WorkspaceEvents.purchaseCredits,
      props: { tokenPackage: creditPackage },
    });

    if (!stripe || !elements || !companyId) {
      handleErrorWithToast({
        id: `paymentError-${Date.now().toString()}`,
        title: "Payment process couldn't be started. Please try again later.",
        error: new Error("Payment process couldn't be started"),
      });
      return;
    }
    setIsLoading(true);

    try {
      // Validate the payment element, if there is an error, show a toast
      const { error: submitError } = await elements.submit();
      if (submitError) {
        showToast({
          message: submitError.message ?? "Error submitting payment details.",
          type: "error",
        });
        setIsLoading(false);
        return;
      }

      const { clientSecret } =
        await coreApiClient.V3.SDB.createStripePaymentIntent({
          workspaceId: companyId,
          productId: creditPackage.id,
        });

      const paymentElement = elements.getElement(PaymentElement);
      if (!paymentElement) {
        // Showing error toast if payment element is not available
        // That means the payment element is not mounted or not rendered in the DOM
        showToast({
          message: "Payment can't be process now. Please try again later.",
          type: "error",
        });
        setIsLoading(false);
        return;
      }

      // Confirm the payment with the client secret and the payment element
      const result: PaymentIntentResult = await stripe.confirmPayment({
        clientSecret,
        elements,
        redirect: "if_required",
      });

      if (result.error) {
        handleErrorWithToast({
          id: `paymentError-${Date.now().toString()}`,
          title: result.error.message ?? "Error processing payment.",
          error: result.error,
        });
      } else if (result.paymentIntent.status === "succeeded") {
        showToast({
          message: "Payment succeeded!",
          type: "success",
        });

        if (companyId) {
          navigateToSettingsPage({
            companyId: companyId,
            settingsTab: SettingsTabs.subscription,
          });
        }
      }
    } catch (error) {
      handleErrorWithToast({
        id: `paymentError-${Date.now().toString()}`,
        title: "Error processing payment. Please try again later.",
        error,
      });
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <PaymentElement />
      <FaroButton
        disabled={!stripe || isLoading}
        isLoading={isLoading}
        type="submit"
        fullWidth
        sx={{ mt: 5 }}
      >
        Pay now
      </FaroButton>
    </Box>
  );
}
